"use client";

import { IconArrowRotateClockwiseOutline18, IconCodeBranchOutline18, IconLink2Outline18 } from "nucleo-ui-outline-18";
import { MemoizedMarkdown } from "@/components/ui/MemoizedMarkdown";
import { WebhookUrlStatus } from "@/components/ui/webhook-url-status";
import { WebhookUrlSection } from "@/components/ui/webhook-url-section";
import { WorkflowStatePanel } from "@/components/ui/workflow-state-panel";
import { WorkflowStepsList, type WorkflowBranch, type WorkflowStep } from "@/components/ui/workflow-step-row";
import { cn } from "@/lib/utils";
import { useActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useWalletsStore } from "@/stores/walletsStore";
import { useWorkflowWebhookSecret, useRegenerateWebhookSecret } from "@/hooks/useWorkflowWebhookSecret";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import type { Workflow } from "@/stores/workflowsStore";
import { useWorkflow } from "../_hooks/useWorkflow";
import type { Edge, Node } from "@xyflow/react";
import type { WorkflowEdgeData } from "./WorkflowEdge";
import { useParams } from "next/navigation";
import type React from "react";
import { useMemo } from "react";

type WorkflowSummaryWorkflow = Pick<Workflow, "id" | "name" | "description" | "type">;

interface WorkflowSummaryViewProps {
  nodes: Node[];
  edges: Edge[];
  workflowName?: string;
  workflowDescription?: string;
  /** Pass workflow directly when available. Required with publicReadOnly when workflow metadata is needed. */
  workflow?: WorkflowSummaryWorkflow | null;
  /** Additional classes for the outer container */
  className?: string;
  /** Additional classes for the scrollable content area */
  contentClassName?: string;
  /** Optional version selector rendered above the header */
  versionSelector?: React.ReactNode;
  /** Public read-only mode disables org-authenticated workflow and webhook metadata fetches. */
  publicReadOnly?: boolean;
}

export default function WorkflowSummaryView({
  nodes,
  edges,
  workflowName,
  workflowDescription,
  workflow: workflowProp,
  className,
  contentClassName,
  versionSelector,
  publicReadOnly = false,
}: WorkflowSummaryViewProps) {
  const { triggers } = useTriggers();
  const { actions } = useActions();
  const queryParams = useParams();
  const workflowId = queryParams?.id as string | undefined;
  // Only fetch if the caller didn't pass the workflow directly — avoids a
  // duplicate network request on the edit page (which already owns the
  // fresh workflow in `workflowProp`).
  const workflowQuery = useWorkflow(workflowId ?? null, { enabled: !publicReadOnly && !workflowProp && !!workflowId });
  const workflowFromCache = workflowQuery.data;

  // Prefer passed workflow prop (has fresh data), fall back to shared cache
  const workflow = workflowProp || workflowFromCache;

  // Check if this is a manual workflow (supports webhook triggering)
  const isManualWorkflow = workflow?.type === "manual";
  const effectiveWorkflowId = !publicReadOnly && isManualWorkflow ? workflow?.id : undefined;
  const {
    secret: workflowWebhookSecret,
    isLoading: isLoadingSecret,
    error: secretError,
  } = useWorkflowWebhookSecret(effectiveWorkflowId);
  const { mutate: regenerateSecret, isPending: isRegenerating } = useRegenerateWebhookSecret(effectiveWorkflowId);
  const canShowWebhookInfo = !publicReadOnly && isManualWorkflow && workflow?.id;
  const hasWebhookInfo = canShowWebhookInfo && workflowWebhookSecret;

  // Extract inputSchema from the trigger (root) node's payload for the webhook curl example.
  const triggerInputSchema = useMemo(() => {
    const rootNode = nodes.find(n => n.id === "root");
    return rootNode?.data?.payload
      ? ((rootNode.data.payload as Record<string, unknown>).inputSchema as Record<string, unknown> | undefined)
      : undefined;
  }, [nodes]);

  // Read connector/wallet stores once at parent level to resolve labels
  // (avoids N per-row Zustand subscriptions in WorkflowStepRow)
  const connectors = useConnectorsStore(state => state.connectors);
  const wallets = useWalletsStore(state => state.wallets);

  // Get execution state to auto-expand branches that ran
  const nodeStates = useRunExecutionStore(state => state.currentRun?.nodeStates);

  // Convert nodes and edges to ordered workflow steps (tree structure for branching)
  const orderedSteps = useMemo(() => {
    if (nodes.length === 0) {
      return [];
    }

    // Filter out placeholder nodes
    const realNodes = nodes.filter(n => n.type !== "placeholder");
    const realEdges = edges.filter(e => !e.target.startsWith("placeholder-"));

    // Create a map of trigger and action types
    const triggerTypes = new Set(triggers.map((t: { type: string }) => t.type));

    // Find the root node (node with no incoming edges)
    const incomingEdges = new Set(realEdges.map(edge => edge.target));
    const rootNode = realNodes.find(n => !incomingEdges.has(n.id));

    if (!rootNode) {
      return [];
    }

    // Build edge map preserving sourceHandle for branch detection
    const edgeMap = new Map<string, { target: string; sourceHandle?: string }[]>();
    realEdges.forEach(edge => {
      const entries = edgeMap.get(edge.source) || [];
      entries.push({ target: edge.target, sourceHandle: edge.sourceHandle ?? undefined });
      edgeMap.set(edge.source, entries);
    });

    const visitedNodes = new Set<string>();

    // Whether we have execution data to determine which branches ran
    const hasExecutionData = !!nodeStates && Object.keys(nodeStates).length > 0;

    // Check if any node in a list of step node IDs executed
    const shouldExpandBranch = (stepNodeIds: string[]): boolean => {
      // No execution data → expand all branches by default
      if (!hasExecutionData) return true;
      // With execution data → only expand branches that ran
      return stepNodeIds.some(id => {
        const node = realNodes.find(n => n.id === id);
        if (!node) return false;
        const apiNodeId = (node.data.apiNodeId as string) || node.id;
        const state = nodeStates![apiNodeId];
        return !!state && state.status !== "idle";
      });
    };

    // Build a tree of steps, following linear chains and splitting at branches
    const buildStepTree = (nodeId: string): WorkflowStep[] => {
      const steps: WorkflowStep[] = [];
      let currentId: string | null = nodeId;

      while (currentId && !visitedNodes.has(currentId)) {
        visitedNodes.add(currentId);
        const node = realNodes.find(n => n.id === currentId);
        if (!node) break;

        const nodeType = (node.data.type as string) || node.type;
        const isTrigger = triggerTypes.has(nodeType) || node.type === "trigger";
        const isLogicNode = node.type === "logic";
        const isIfNode = isLogicNode && nodeType === "if";
        const isWaitNode = isLogicNode && nodeType === "wait";
        const isForEachNode = isLogicNode && nodeType === "for-each";

        const definition = isTrigger
          ? triggers.find((t: { type: string }) => t.type === nodeType)
          : actions.find((a: { type: string }) => a.type === nodeType);

        const nodeConnector = node.data.connector as { id?: string; type: string } | undefined;

        // Resolve connector/wallet label at parent level
        const connectorId = nodeConnector?.id;
        const connectorInstance = connectorId ? connectors.find(c => c.id === connectorId) : undefined;
        const walletInstance = !connectorInstance && connectorId ? wallets.find(w => w.id === connectorId) : undefined;

        const titleOverride = node.data.titleOverride as string | undefined;
        const nodePayload = (node.data.payload as Record<string, unknown>) || {};
        const step: WorkflowStep = {
          id: currentId,
          name: (node.data.name as string) || definition?.name || nodeType,
          titleOverride: titleOverride,
          type: nodeType || "",
          isTrigger: isTrigger || node.type === "trigger",
          description: node.data.description as string | undefined,
          payload: nodePayload,
          connector: nodeConnector,
          connectorLabel: connectorInstance?.name || walletInstance?.name,
          walletAddress: walletInstance?.address,
          icon: isLogicNode ? (
            isForEachNode ? (
              <IconArrowRotateClockwiseOutline18 className="h-4 w-4" />
            ) : (
              <IconCodeBranchOutline18 className="h-4 w-4" />
            )
          ) : undefined,
          badge: isLogicNode ? "Logic" : undefined,
        };

        const children: { target: string; sourceHandle?: string }[] = edgeMap.get(currentId) || [];

        if (isIfNode && children.length > 0) {
          // Build then/else branches
          const thenEdge = children.find((c: { sourceHandle?: string }) => c.sourceHandle === "source-then");
          const elseEdge = children.find((c: { sourceHandle?: string }) => c.sourceHandle === "source-else");

          const branches: WorkflowBranch[] = [];
          if (thenEdge) {
            const thenSteps = buildStepTree(thenEdge.target);
            const thenNodeIds = thenSteps.map(s => s.id);
            branches.push({
              label: "Then",
              color: "green",
              steps: thenSteps,
              defaultExpanded: shouldExpandBranch(thenNodeIds),
            });
          }
          if (elseEdge) {
            const elseSteps = buildStepTree(elseEdge.target);
            const elseNodeIds = elseSteps.map(s => s.id);
            branches.push({
              label: "Else",
              color: "red",
              steps: elseSteps,
              defaultExpanded: shouldExpandBranch(elseNodeIds),
            });
          }
          if (branches.length > 0) {
            step.branches = branches;
          }
          steps.push(step);
          currentId = null; // Branches handle their own children
        } else if (
          isWaitNode &&
          children.length > 0 &&
          children.some(c => c.sourceHandle === "source-resumed" || c.sourceHandle === "source-timed_out")
        ) {
          // Build resumed/timed_out branches for wait nodes
          const resumedEdge = children.find((c: { sourceHandle?: string }) => c.sourceHandle === "source-resumed");
          const timedOutEdge = children.find((c: { sourceHandle?: string }) => c.sourceHandle === "source-timed_out");

          const branches: WorkflowBranch[] = [];
          if (resumedEdge) {
            const resumedSteps = buildStepTree(resumedEdge.target);
            const resumedNodeIds = resumedSteps.map(s => s.id);
            branches.push({
              label: "Resumed",
              color: "green",
              steps: resumedSteps,
              defaultExpanded: shouldExpandBranch(resumedNodeIds),
            });
          }
          if (timedOutEdge) {
            const timedOutSteps = buildStepTree(timedOutEdge.target);
            const timedOutNodeIds = timedOutSteps.map(s => s.id);
            branches.push({
              label: "Timed Out",
              color: "amber",
              steps: timedOutSteps,
              defaultExpanded: shouldExpandBranch(timedOutNodeIds),
            });
          }
          if (branches.length > 0) {
            step.branches = branches;
          }
          steps.push(step);
          currentId = null; // Branches handle their own children
        } else if (isForEachNode && children.length > 0) {
          // Separate body children from onComplete children
          const onCompleteEdges = realEdges.filter(
            e => e.source === currentId && (e.data as WorkflowEdgeData)?.isOnComplete,
          );
          const onCompleteTargets = new Set(onCompleteEdges.map(e => e.target));
          const bodyChildren = children.filter(c => !onCompleteTargets.has(c.target));

          const branches: WorkflowBranch[] = [];
          if (bodyChildren.length > 0) {
            const bodySteps = buildStepTree(bodyChildren[0].target);
            const bodyNodeIds = bodySteps.map(s => s.id);
            branches.push({
              label: "Body",
              color: "purple",
              steps: bodySteps,
              defaultExpanded: shouldExpandBranch(bodyNodeIds),
            });
          }
          if (onCompleteTargets.size > 0) {
            const ocSteps: WorkflowStep[] = [];
            for (const ocTarget of onCompleteTargets) {
              ocSteps.push(...buildStepTree(ocTarget));
            }
            const ocNodeIds = ocSteps.map(s => s.id);
            branches.push({
              label: "On Complete",
              color: "amber",
              steps: ocSteps,
              defaultExpanded: shouldExpandBranch(ocNodeIds),
            });
          }
          if (branches.length > 0) {
            step.branches = branches;
          }
          steps.push(step);
          currentId = null;
        } else if (children.length === 1) {
          // Linear chain: continue walking
          steps.push(step);
          currentId = children[0].target;
        } else {
          // No children or multiple non-logic children
          steps.push(step);
          currentId = null;
        }
      }

      return steps;
    };

    return buildStepTree(rootNode.id);
  }, [nodes, edges, triggers, actions, nodeStates, connectors, wallets]);

  if (orderedSteps.length === 0) {
    return (
      <div className="flex h-full bg-zinc-50 transition-all duration-300 dark:bg-zinc-950">
        <div className="flex flex-1 items-center justify-center transition-all duration-300">
          <WorkflowStatePanel
            icon={IconCodeBranchOutline18}
            title="No workflow steps yet"
            description="Add a trigger to get started building your workflow."
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full max-h-[calc(100dvh-72px)] bg-zinc-50 transition-all duration-300 dark:bg-zinc-950",
        className,
      )}
    >
      <div className={cn("flex-1 overflow-y-auto p-8 transition-all duration-300", contentClassName)}>
        <div className="mx-auto max-w-4xl">
          {versionSelector && <div className="mb-6">{versionSelector}</div>}

          {/* Workflow Header */}
          <div className="mb-8">
            <h1 className="text-ds-text-primary text-2xl font-bold">
              {workflowName || workflow?.name || "Untitled Workflow"}
            </h1>
            {(workflowDescription || workflow?.description) && (
              <div className="text-ds-text-subtle mt-3 text-sm">
                <MemoizedMarkdown
                  content={workflowDescription || workflow?.description || ""}
                  id={workflow?.id || "workflow-summary-desc"}
                />
              </div>
            )}
          </div>

          {/* Webhook URL Section (for manual trigger workflows) */}
          {hasWebhookInfo && (
            <div className="mb-8">
              <WebhookUrlSection
                workflowId={workflow.id}
                secret={workflowWebhookSecret!}
                inputSchema={triggerInputSchema}
                onRegenerateSecret={() => regenerateSecret()}
                isRegenerating={isRegenerating}
              />
            </div>
          )}
          {canShowWebhookInfo && !workflowWebhookSecret && isLoadingSecret && (
            <div className="border-ds-border-default dark:bg-ds-neutral-900 bg-ds-surface-secondary mb-8 rounded-lg border p-4">
              <div className="mb-3 flex items-center gap-2">
                <IconLink2Outline18 className="text-ds-text-muted h-4 w-4" />
                <h3 className="text-ds-text-primary text-sm font-semibold dark:text-zinc-100">Webhook URL</h3>
              </div>
              <WebhookUrlStatus message="Loading webhook URL..." className="bg-ds-surface-card" />
            </div>
          )}
          {canShowWebhookInfo && !workflowWebhookSecret && secretError && (
            <div className="mb-8 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
              <div className="mb-3 flex items-center gap-2">
                <IconLink2Outline18 className="h-4 w-4 text-red-600 dark:text-red-400" />
                <h3 className="text-sm font-semibold text-red-900 dark:text-red-100">Webhook URL</h3>
              </div>
              <WebhookUrlStatus
                message="Failed to load webhook secret"
                tone="error"
                className="bg-white dark:bg-red-900/20"
              />
            </div>
          )}

          <WorkflowStepsList steps={orderedSteps} linkToDetail={false} defaultExpanded={false} />
        </div>
      </div>
    </div>
  );
}
